import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { useAppSelector } from "app/hooks";
import { selectCurrentUser } from "features/auth/auth-slice";
import type { DeskPhone } from "services/dialstack";

import {
  deskPhoneActionsSx,
  deskPhonePrimaryButtonSx,
  deskPhoneStateBodySx,
  deskPhoneTextButtonSx,
  walkUpBannerSx
} from "../dialstack-softphone-widget.styles";
import {
  NOT_NOW_LABEL,
  walkUpButtonLabel,
  walkUpLine
} from "./desk-phone-copy";
import type { DeskPhonesController } from "./use-desk-phones.hook";
import useRememberedDesk, {
  dismissPrompt,
  isPromptDismissed
} from "./use-remembered-desk.hook";

type Props = {
  controller: DeskPhonesController;
  onLogIn: (phone: DeskPhone) => void;
};

// The walk-up prompt (ENG-2293): this browser remembers the phone that sits at
// this desk, so when it is somebody else's — their own, or logged in by them —
// the panel offers it in one line and one click. It never opens the panel by
// itself, "Not now" lasts the browser session for the person who said it, and
// a phone that is gone from the account is forgotten.
export default function DeskPhoneWalkUpBanner({ controller, onLogIn }: Props) {
  const userId = useAppSelector(selectCurrentUser)?.id ?? null;
  const { desk, forget } = useRememberedDesk();
  const [dismissed, setDismissed] = useState(() =>
    desk ? isPromptDismissed(desk.deviceId, userId) : false
  );
  const phone = desk
    ? (controller.phones.find((candidate) => candidate.id === desk.deviceId) ??
      null)
    : null;
  const gone =
    desk !== null &&
    phone === null &&
    !controller.isLoading &&
    !controller.isError &&
    controller.phones.length > 0;
  useEffect(() => {
    if (gone) forget();
  }, [gone, forget]);

  if (!phone || phone.mine || dismissed || controller.request) return null;

  return (
    <Box role="status" sx={walkUpBannerSx} data-testid="desk-phone-walk-up">
      <Typography sx={deskPhoneStateBodySx}>{walkUpLine(phone)}</Typography>
      <Box sx={deskPhoneActionsSx}>
        <Button
          variant="contained"
          size="small"
          className="suno-phone-no-drag"
          onClick={() => onLogIn(phone)}
          sx={deskPhonePrimaryButtonSx}
        >
          {walkUpButtonLabel(phone)}
        </Button>
        <Button
          variant="text"
          size="small"
          className="suno-phone-no-drag"
          onClick={() => {
            dismissPrompt(phone.id, userId);
            setDismissed(true);
          }}
          sx={deskPhoneTextButtonSx}
        >
          {NOT_NOW_LABEL}
        </Button>
      </Box>
    </Box>
  );
}
